/* ============================================
   Custom Product Modules - JavaScript
   Vanilla JS, No jQuery
   ============================================ */

(function() {
  'use strict';

  /**
   * Tab Module Controller
   * Handles tab switching with scope isolation for multiple instances
   */
  class CPMTabs {
    constructor(container) {
      this.container = container;
      this.buttons = container.querySelectorAll('.cpm-tabs__btn');
      this.contents = container.querySelectorAll('.cpm-tabs__content');
      
      if (this.buttons.length === 0) return;
      
      this.init();
    }

    init() {
      this.buttons.forEach((btn, index) => {
        btn.addEventListener('click', () => this.switchTab(index));
      });

      // Activate first tab by default if none is active
      if (!this.container.querySelector('.cpm-tabs__btn.active')) {
        this.switchTab(0);
      }
    }

    switchTab(index) {
      // Deactivate all tabs within this container
      this.buttons.forEach(btn => btn.classList.remove('active'));
      this.contents.forEach(content => content.classList.remove('active'));

      // Activate selected tab
      if (this.buttons[index]) {
        this.buttons[index].classList.add('active');
      }
      if (this.contents[index]) {
        this.contents[index].classList.add('active');
      }
    }
  }

  /**
   * Initialize all tab modules on the page
   */
  function initAllTabs() {
    const tabContainers = document.querySelectorAll('.cpm-tabs');
    tabContainers.forEach(container => {
      // Prevent re-initialization
      if (!container.dataset.cpmInitialized) {
        new CPMTabs(container);
        container.dataset.cpmInitialized = 'true';
      }
    });
  }

  /**
   * Initialize on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllTabs);
  } else {
    initAllTabs();
  }

  /**
   * Re-initialize when Shopify sections are loaded/reloaded
   * This handles Theme Editor and AJAX section rendering
   */
  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target;
    const tabContainers = section.querySelectorAll('.cpm-tabs');
    tabContainers.forEach(container => {
      container.dataset.cpmInitialized = '';
      new CPMTabs(container);
      container.dataset.cpmInitialized = 'true';
    });
  });

  /**
   * Helper to extract text from Shopify Rich Text JSON structure
   */
  function extractTextFromRichText(node) {
    if (!node) return '';
    
    if (node.type === 'text') {
      return node.value || '';
    }
    
    let content = '';
    if (node.children && Array.isArray(node.children)) {
      content = node.children.map(extractTextFromRichText).join('');
    }
    
    // Add newlines for block elements to preserve structure
    if (node.type === 'paragraph' || node.type === 'heading') {
      return content + '\n\n';
    }
    if (node.type === 'list-item') {
      return content + '\n';
    }
    
    return content;
  }

  /**
   * Markdown Renderer
   * Renders markdown content using marked.js
   * Supports both raw text and Shopify Rich Text JSON
   */
  function renderMarkdown() {
    if (typeof marked === 'undefined') return;

    const elements = document.querySelectorAll('[data-markdown]:not([data-markdown-rendered])');
    elements.forEach(el => {
      // Unescape HTML entities from Liquid escape filter
      let raw = el.textContent
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");

      // Check if content is Shopify Rich Text JSON
      let markdownSource = raw;
      try {
        if (raw.trim().startsWith('{') && raw.includes('"type":"root"')) {
          const json = JSON.parse(raw);
          if (json.type === 'root') {
            markdownSource = extractTextFromRichText(json);
          }
        }
      } catch (e) {
        // Not JSON or parsing failed, fallback to raw text
        console.debug('CPM: Markdown source is not valid JSON, treating as plain text.');
      }

      el.innerHTML = marked.parse(markdownSource);
      el.setAttribute('data-markdown-rendered', 'true');
    });
  }

  /**
   * Wait for marked.js to load, then render
   */
  function initMarkdown() {
    if (typeof marked !== 'undefined') {
      renderMarkdown();
    } else {
      // Retry after a short delay if marked.js hasn't loaded yet
      setTimeout(initMarkdown, 100);
    }
  }

  /**
   * Initialize on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMarkdown);
  } else {
    initMarkdown();
  }

  /**
   * Add to Cart Handler
   * Handles quick add-to-cart for product cards
   */
  function initAddToCart() {
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('.cpm-tab-block__product-atc');
      if (!btn || btn.disabled) return;

      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      // Disable button and show loading state
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = '...';

      // Add to cart via Shopify AJAX API
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Add to cart failed');
        }
        return response.json();
      })
      .then(() => {
        btn.textContent = '✓';
        
        // Dispatch event for cart drawer or other listeners
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        // Reset button after delay
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);
      })
      .catch(() => {
        btn.textContent = '✕';
        setTimeout(() => {
          btn.textContent = originalText;
          btn.disabled = false;
        }, 1500);
      });
    });
  }

  /**
   * Initialize Add to Cart on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAddToCart);
  } else {
    initAddToCart();
  }

  /**
   * Modal Handler
   * Handles opening/closing modals for image cards
   */
  function initModals() {
    // Open modal on card click
    document.addEventListener('click', function(e) {
      const trigger = e.target.closest('[data-modal-trigger]');
      if (!trigger) return;

      const modalId = trigger.dataset.modalTrigger;
      const modal = document.querySelector(`[data-modal="${modalId}"]`);
      if (modal) {
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
    });

    // Close modal on close button click
    document.addEventListener('click', function(e) {
      if (e.target.closest('.cpm-modal__close')) {
        const modal = e.target.closest('.cpm-modal');
        if (modal) {
          modal.classList.remove('active');
          document.body.style.overflow = '';
        }
      }
    });

    // Close modal on backdrop click
    document.addEventListener('click', function(e) {
      if (e.target.classList.contains('cpm-modal')) {
        e.target.classList.remove('active');
        document.body.style.overflow = '';
      }
    });

    // Close modal on ESC key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        const activeModal = document.querySelector('.cpm-modal.active');
        if (activeModal) {
          activeModal.classList.remove('active');
          document.body.style.overflow = '';
        }
      }
    });
  }

  /**
   * Initialize Modals on DOM ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initModals);
  } else {
    initModals();
  }

  // Expose to global scope for manual initialization if needed
  window.CPMTabs = CPMTabs;
  window.initCPMTabs = initAllTabs;
  window.renderCPMMarkdown = renderMarkdown;

})();
